import React, { useCallback, useState } from 'react';
import classNames from 'classnames';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons/faInfoCircle';

import Button, { BUTTON_TYPES } from 'buyside/components/presentation/Button/button';
import AboutQuickPositions from 'buyside/components/presentation/QuickPositionsOverview/aboutQuickPositions';
import CloseQuickPositionModal from 'buyside/components/presentation/QuickPosition/CloseQuickPositionModal/closeQuickPositionModal';
import Link from 'buyside/components/presentation/Link/link';
import Modal from 'buyside/components/presentation/Modal/modal';
import Skeleton, { SKELETON_WIDTHS } from 'buyside/components/presentation/Skeleton/skeleton';

import { entityUrl } from 'buyside/util/urlHelper';

import { useCurrentUserQuickPositionsQuery } from 'interfaces/graphql-typings';

import styles from './quickPositionsOverviewStyles.scss';

const QuickPositionsOverview: React.FC<{ hasPositions: boolean }> = ({ hasPositions }) => {
  const [isAboutQuickPositionsModalOpen, setIsAboutQuickPositionsModalOpen] = useState(false);

  const { data, error, loading } = useCurrentUserQuickPositionsQuery({ skip: !hasPositions });

  const renderOpenAboutQuickPositionsModalButton = useCallback(
    () => (
      <Button
        className={styles.aboutQuickPositionsButton}
        faIcon={faInfoCircle}
        onClick={() => setIsAboutQuickPositionsModalOpen(true)}
        text="About Quick Positions"
        type={BUTTON_TYPES.LINK}
      />
    ),
    []
  );

  if (loading)
    return (
      <div className={styles.quickPositionsOverview}>
        <table>
          <tbody>
            {[...Array(10)].map((_e, rowIndex) => (
              <tr key={rowIndex + '-skeleton-row'}>
                {['ticker', 'direction', 'latest price', 'opened', 'closed', 'return'].map(
                  dataCell => (
                    <td data-cell={dataCell} key={`${rowIndex}-${dataCell}-skeleton-cell`}>
                      <div>
                        <p>
                          <Skeleton width={SKELETON_WIDTHS.SMALL} />
                        </p>
                        {dataCell === 'opened' || dataCell === 'closed' ? (
                          <p>
                            <Skeleton width={SKELETON_WIDTHS.SMALL} />
                          </p>
                        ) : null}
                      </div>
                    </td>
                  )
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );

  if (error) throw error;

  const quickPositions = data?.currentUser?.quickPositions;

  return (
    <div className={styles.quickPositionsOverview}>
      {quickPositions?.length ? (
        <>
          <Modal
            isOpen={isAboutQuickPositionsModalOpen}
            onClose={() => setIsAboutQuickPositionsModalOpen(false)}
            renderOpenModalButton={renderOpenAboutQuickPositionsModalButton}
            size="lg"
            title="About Quick Positions"
          >
            <AboutQuickPositions />
          </Modal>
          <table>
            <tbody>
              <tr>
                <th>Ticker</th>
                <th>Direction</th>
                <th>Latest Price</th>
                <th>Opened At</th>
                <th>Closed At</th>
                <th>Return</th>
              </tr>
              {quickPositions?.map(quickPosition => {
                if (!quickPosition) return null;

                const {
                  id: quickPositionId,
                  asset: { id: assetId, currencyCode, entityId, latestPrice, symbol },
                  closedAt,
                  closingPrice,
                  direction,
                  openedAt,
                  openingPrice,
                  returnToDate
                } = quickPosition;

                return (
                  <tr key={`${quickPosition.id}-table-row`}>
                    <td data-cell="ticker">
                      <Link className={styles.goldChip} to={entityUrl(entityId)}>
                        {symbol}
                      </Link>
                    </td>
                    <td data-cell="direction">
                      <p
                        className={classNames({
                          [styles.long]: direction === 'long'
                        })}
                      >
                        {direction === 'long' ? <>Long</> : <>Short</>}
                      </p>
                    </td>
                    <td data-cell="latest price">
                      <p>
                        {latestPrice ? (
                          <>
                            {latestPrice} {currencyCode}
                          </>
                        ) : (
                          <i>Pending...</i>
                        )}
                      </p>
                    </td>
                    <td data-cell="opened at">
                      <div>
                        <p>
                          {openingPrice} {currencyCode}
                        </p>
                        <p>{openedAt}</p>
                      </div>
                    </td>
                    <td data-cell="closed at">
                      {closedAt ? (
                        <div>
                          <p>
                            {closingPrice} {currencyCode}
                          </p>
                          <p>{closedAt}</p>
                        </div>
                      ) : (
                        <CloseQuickPositionModal
                          assetId={assetId}
                          currencyCode={currencyCode}
                          forTable
                          latestPrice={latestPrice}
                          quickPositionId={quickPositionId}
                          symbol={symbol}
                        />
                      )}
                    </td>
                    <td data-cell="return">
                      <p
                        className={classNames({
                          [styles.positive]: (returnToDate || 0) > 0
                        })}
                      >
                        {returnToDate ? `${returnToDate}%` : null}
                      </p>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      ) : (
        <AboutQuickPositions />
      )}
    </div>
  );
};

export default QuickPositionsOverview;
